import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Container, Table, Alert, Badge } from 'react-bootstrap';
import { Bar, Line, Pie, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const DataDistributionDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [distributionData, setDistributionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDataDistribution();
  }, []);

  const fetchDataDistribution = async () => {
    try {
      setLoading(true);
      const response = await axiosPrivate.get('/Clustering/dataDistribution');
      setDistributionData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load data distribution analysis');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center p-5">Loading data distribution analysis...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!distributionData) return null;

  const yearlyChartData = {
    labels: Object.keys(distributionData.yearlyDistribution || {}).sort(),
    datasets: [{
      label: 'M&A Events by Year',
      data: Object.keys(distributionData.yearlyDistribution || {})
        .sort()
        .map(year => distributionData.yearlyDistribution[year]),
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1
    }]
  };

  const businessModelData = {
    labels: Object.keys(distributionData.businessModelDistribution || {}),
    datasets: [{
      data: Object.values(distributionData.businessModelDistribution || {}),
      backgroundColor: [
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 206, 86, 0.6)',
        'rgba(75, 192, 192, 0.6)',
        'rgba(153, 102, 255, 0.6)',
        'rgba(255, 159, 64, 0.6)'
      ]
    }]
  };

  const lifetimeBoxPlotData = {
    labels: ['Distribution'],
    datasets: [{
      label: 'Lifetime (days)',
      data: distributionData.lifetimeStats ? [{
        x: 'Lifetime',
        min: distributionData.lifetimeStats.min,
        q1: distributionData.lifetimeStats.q1,
        median: distributionData.lifetimeStats.median,
        q3: distributionData.lifetimeStats.q3,
        max: distributionData.lifetimeStats.max,
        mean: distributionData.lifetimeStats.mean
      }] : [],
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1
    }]
  };

  return (
    <Container fluid className="p-4">
      <h2 className="mb-4">Data Distribution Analysis</h2>

      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card>
            <Card.Body>
              <h6 className="text-muted">Total Records</h6>
              <h3>{distributionData.totalRecords}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card>
            <Card.Body>
              <h6 className="text-muted">Data Completeness</h6>
              <h3>{distributionData.dataCompleteness?.toFixed(1)}%</h3>
              <div className="progress mt-2">
                <div
                  className="progress-bar bg-success"
                  style={{width: `${distributionData.dataCompleteness}%`}}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card>
            <Card.Body>
              <h6 className="text-muted">Years Covered</h6>
              <h3>{distributionData.yearsCovered || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card>
            <Card.Body>
              <h6 className="text-muted">Quality Issues</h6>
              <h3>{distributionData.qualityIssues?.length || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h5>Temporal Distribution</h5>
            </Card.Header>
            <Card.Body>
              <Bar
                data={yearlyChartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    title: {
                      display: true,
                      text: 'M&A Events Over Time'
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Events'
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Year'
                      }
                    }
                  }
                }}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h5>Business Model Distribution</h5>
            </Card.Header>
            <Card.Body>
              <Pie
                data={businessModelData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: 'right' },
                    title: {
                      display: true,
                      text: 'Distribution by Business Model Type'
                    }
                  }
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h5>Lifetime Statistics</h5>
            </Card.Header>
            <Card.Body>
              {distributionData.lifetimeStats && (
                <Table striped bordered hover size="sm">
                  <tbody>
                    <tr>
                      <td><strong>Mean</strong></td>
                      <td>{distributionData.lifetimeStats.mean.toFixed(2)} days</td>
                    </tr>
                    <tr>
                      <td><strong>Median</strong></td>
                      <td>{distributionData.lifetimeStats.median.toFixed(2)} days</td>
                    </tr>
                    <tr>
                      <td><strong>Std Dev</strong></td>
                      <td>{distributionData.lifetimeStats.stdDev.toFixed(2)} days</td>
                    </tr>
                    <tr>
                      <td><strong>Min</strong></td>
                      <td>{distributionData.lifetimeStats.min.toFixed(2)} days</td>
                    </tr>
                    <tr>
                      <td><strong>Max</strong></td>
                      <td>{distributionData.lifetimeStats.max.toFixed(2)} days</td>
                    </tr>
                    <tr>
                      <td><strong>Q1 (25th percentile)</strong></td>
                      <td>{distributionData.lifetimeStats.q1.toFixed(2)} days</td>
                    </tr>
                    <tr>
                      <td><strong>Q3 (75th percentile)</strong></td>
                      <td>{distributionData.lifetimeStats.q3.toFixed(2)} days</td>
                    </tr>
                    <tr>
                      <td><strong>Skewness</strong></td>
                      <td>{distributionData.lifetimeStats.skewness.toFixed(3)}</td>
                    </tr>
                    <tr>
                      <td><strong>Kurtosis</strong></td>
                      <td>{distributionData.lifetimeStats.kurtosis.toFixed(3)}</td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h5>Technology Impact Score Statistics</h5>
            </Card.Header>
            <Card.Body>
              {distributionData.techImpactStats && (
                <Table striped bordered hover size="sm">
                  <tbody>
                    <tr>
                      <td><strong>Mean</strong></td>
                      <td>{distributionData.techImpactStats.mean.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td><strong>Median</strong></td>
                      <td>{distributionData.techImpactStats.median.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td><strong>Std Dev</strong></td>
                      <td>{distributionData.techImpactStats.stdDev.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td><strong>Min</strong></td>
                      <td>{distributionData.techImpactStats.min.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td><strong>Max</strong></td>
                      <td>{distributionData.techImpactStats.max.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td><strong>Q1 (25th percentile)</strong></td>
                      <td>{distributionData.techImpactStats.q1.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td><strong>Q3 (75th percentile)</strong></td>
                      <td>{distributionData.techImpactStats.q3.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td><strong>Skewness</strong></td>
                      <td>{distributionData.techImpactStats.skewness.toFixed(3)}</td>
                    </tr>
                    <tr>
                      <td><strong>Kurtosis</strong></td>
                      <td>{distributionData.techImpactStats.kurtosis.toFixed(3)}</td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {distributionData.qualityIssues && distributionData.qualityIssues.length > 0 && (
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <h5>Data Quality Issues</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Issue</th>
                      <th>Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributionData.qualityIssues.map((issue, idx) => (
                      <tr key={idx}>
                        <td>{issue.field}</td>
                        <td>{issue.issue}</td>
                        <td>{issue.count}</td>
                        <td>
                          <Badge bg={issue.percentage > 10 ? 'danger' : 'warning'}>
                            {issue.percentage.toFixed(2)}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default DataDistributionDashboard;